import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MSelectCard } from '@/components/ui/atoms/MSelectCard';
import MHeader from '@/components/ui/organisms/MHeader';
import { getMemberInfo } from '@/hooks/useMembers';
import MSpacer from '@/components/ui/atoms/MSpacer';
import { useTranslation } from 'react-i18next';
import styles from './insurance.module.css';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES, getInsuranceScreenName } from '@/constants/analytics.constants';

const AddInsurance: React.FC = () => {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const [searchParams] = useSearchParams();
  const count = parseInt(searchParams.get('count') || '0', 10);

  // Track GTM user_data_loaded event on page load
  useEffect(() => {
    if (!memberInfo?.id) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
      screenName: ANALYTICS_SCREEN_NAMES.PROFILE_BASIC_INFO_INSURANCE_ADD_POLICY,
    });
  }, [memberInfo?.id]);

  const handleSelectType = (type: string) => {
    setSelectedType(type);
    
    // Track insurance type selection
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
      screenName: getInsuranceScreenName(type as 'private' | 'government' | 'family', 'select'),
    });
    
    if (type === 'family') {
      navigate(`/profile/basic-information/insurance/link-family?memberId=${memberInfo.id}`);
    } else {
      navigate('/profile/basic-information/insurance/form', { state: { insuranceType: type } });
    }
  };

  return (
    <div className="profile-page-root">
      <MHeader
        module="add_insurance"
        memberInfo={memberInfo}
      />
      <MSpacer />
      <div className="profile-input-container">
        <div className="flex flex-col gap-4 w-full">
          <MSelectCard
            selected={selectedType === 'private'}
            text={t("add_insurance.private")}
            variant="description"
            description={t("add_insurance.privateDescription")}
            onClick={() => handleSelectType('private')}
            className={["w-full", styles.selectOptions].join(' ')}
          />
          <MSelectCard
            selected={selectedType === 'government'}
            text={t("add_insurance.government")}
            variant="description"
            description={t("add_insurance.governmentDescription")}
            onClick={() => handleSelectType('government')}
            className={["w-full", styles.selectOptions].join(' ')}
          />
          <MSelectCard
            selected={selectedType === 'family'}
            text={t("add_insurance.family")}
            variant="description"
            description={t("add_insurance.familyDescription")}
            onClick={() => handleSelectType('family')}
            className={["w-full", styles.selectOptions].join(' ')}
          />
          {count === 0 && (
            <div
              onClick={() => navigate('/profile/basic-information/')}
            >
              <p className={styles.noInsuranceLink}>{t("add_insurance.noInsuranceLink")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddInsurance;
